/**
 * Admin Ops Pages — Office admin (§32), Reports pack (§39), Data migration (§47),
 * Access Matrix (§40).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Pencil, X, KeyRound, FileDown, UploadCloud, ShieldCheck, Check } from 'lucide-react';
import { apiClient, API } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const Card = ({ children, className='' }) => (<div className={`bg-white border border-black/5 rounded-2xl ${className}`}>{children}</div>);
const H1 = ({ eyebrow, title, sub }) => (
  <div className="mb-6"><span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">{eyebrow}</span>
    <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily:'Cormorant Garamond, serif',fontWeight:500 }}>{title}</h1>
    {sub && <p className="mt-1 text-sm text-[#0b1a2e]/60">{sub}</p>}</div>
);
const Btn = ({ variant='primary', className='', ...r }) => {
  const s = { primary:'bg-[#0f5c3d] text-white hover:bg-[#0b4a30]', ghost:'border border-[#0f5c3d]/20 text-[#0f5c3d] hover:bg-[#f7fbf8]' }[variant];
  return <button {...r} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${s} ${className}`}/>;
};
const Input = (p) => <input {...p} className={`w-full px-3 py-2 border border-slate-300 rounded text-sm ${p.className||''}`}/>;
const Select = (p) => <select {...p} className={`w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white ${p.className||''}`}>{p.children}</select>;
const Field = ({ label, children }) => (<div><label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">{label}</label>{children}</div>);

// ============================================================
// OFFICE ADMIN
// ============================================================
export const OfficeAdminPage = () => {
  const [items, setItems] = useState([]); const [editing, setEditing] = useState(null); const [filter, setFilter] = useState('');
  const load = useCallback(async () => { const r = await apiClient.get('/hr/office/resources'); setItems(r.data.data||[]); }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (r) => { if (window.confirm(`Delete ${r.name}?`)) { await apiClient.delete(`/hr/office/resources/${r.id}`); load(); } };
  const filtered = items.filter((i) => !filter || i.kind === filter);
  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <H1 eyebrow="Global HR · §32" title="Office administration" sub="Access cards · keys · meeting rooms · vehicles · insurance · service contracts · supplies." />
        <div className="flex gap-2 mb-6"><Btn onClick={()=>setEditing({})} data-testid="new-office-btn"><Plus className="w-3.5 h-3.5"/> New resource</Btn></div>
      </div>
      <div className="flex gap-2 mb-3">
        {['','access_card','key','meeting_room','vehicle','insurance','service_contract','supplies','other'].map((k)=>(
          <button key={k||'all'} onClick={()=>setFilter(k)} className={`px-3 py-1 rounded-full text-xs ${filter===k?'bg-[#0f5c3d] text-white':'border border-[#0f5c3d]/20 text-[#0f5c3d]'}`}>{k||'all'}</button>
        ))}
      </div>
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60"><tr><th className="text-left p-2">Code</th><th className="text-left p-2">Name</th><th className="text-left p-2">Kind</th><th className="text-left p-2">Location</th><th className="text-left p-2">Expiry</th><th className="text-right p-2">Cost</th><th></th></tr></thead>
          <tbody>{filtered.map((r)=>(
            <tr key={r.id} className="border-t border-black/5" data-testid={`office-row-${r.code}`}>
              <td className="p-2 font-mono text-xs">{r.code}</td>
              <td className="p-2"><KeyRound className="w-3.5 h-3.5 inline text-[#0f5c3d] mr-1.5"/>{r.name}</td>
              <td className="p-2 text-xs">{r.kind}</td>
              <td className="p-2 text-xs">{r.location||'—'} {r.country && `· ${r.country}`}</td>
              <td className="p-2 text-xs">{r.expiry_date||'—'}</td>
              <td className="p-2 text-right text-xs">{r.cost||'—'} {r.currency||''}</td>
              <td className="p-2 text-right"><button onClick={()=>setEditing(r)} className="text-[#0f5c3d] mr-2"><Pencil className="w-3.5 h-3.5 inline"/></button><button onClick={()=>del(r)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 inline"/></button></td>
            </tr>
          ))}</tbody>
        </table>
        {filtered.length===0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No resources.</div>}
      </Card>
      {editing && <OfficeModal initial={editing} onClose={()=>setEditing(null)} onSaved={()=>{setEditing(null);load();}}/>}
    </div>
  );
};

const OfficeModal = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState({ code:'', name:'', kind:'access_card', location:'', country:'', assigned_to:'', expiry_date:'', vendor_id:'', cost:'', currency:'USD', notes:'', active:true, ...initial });
  const save = async () => {
    try { const p = {...f, cost:f.cost?parseFloat(f.cost):null, expiry_date:f.expiry_date||null, code:(f.code||'').toUpperCase()};
      if (f.id) await apiClient.patch(`/hr/office/resources/${f.id}`, p); else await apiClient.post('/hr/office/resources', p);
      toast({title:'Saved'}); onSaved();
    } catch(e){ toast({title:e?.response?.data?.detail||'Failed',variant:'destructive'}); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">{f.id?'Edit':'New'} resource</div><button onClick={onClose}><X className="w-5 h-5"/></button></div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Field label="Code"><Input value={f.code} onChange={(e)=>setF({...f,code:e.target.value})} data-testid="off-code"/></Field>
          <Field label="Kind"><Select value={f.kind} onChange={(e)=>setF({...f,kind:e.target.value})}>{['access_card','key','meeting_room','vehicle','insurance','service_contract','supplies','other'].map((k)=><option key={k} value={k}>{k}</option>)}</Select></Field>
          <div className="col-span-2"><Field label="Name"><Input value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} data-testid="off-name"/></Field></div>
          <Field label="Location"><Input value={f.location||''} onChange={(e)=>setF({...f,location:e.target.value})}/></Field>
          <Field label="Country"><Input value={f.country||''} onChange={(e)=>setF({...f,country:e.target.value.toUpperCase()})}/></Field>
          <Field label="Assigned to"><Input value={f.assigned_to||''} onChange={(e)=>setF({...f,assigned_to:e.target.value})}/></Field>
          <Field label="Expiry"><Input type="date" value={f.expiry_date||''} onChange={(e)=>setF({...f,expiry_date:e.target.value})}/></Field>
          <Field label="Cost"><Input type="number" value={f.cost||''} onChange={(e)=>setF({...f,cost:e.target.value})}/></Field>
          <Field label="Currency"><Input value={f.currency||''} onChange={(e)=>setF({...f,currency:e.target.value.toUpperCase()})}/></Field>
          <div className="col-span-2 flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="off-save">Save</Btn></div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MANAGEMENT REPORTS
// ============================================================
export const ReportsPage = () => {
  const [statutory, setStatutory] = useState([]);
  const [digestSending, setDigestSending] = useState(false);
  useEffect(() => { apiClient.get('/hr/statutory/available').then((r)=>setStatutory(r.data.filings||[])).catch(()=>{}); }, []);
  const reports = [
    { k:'employees', label:'Employees', desc:'Full directory: codes, names, country, department, designation, salary' },
    { k:'payroll', label:'Payroll', desc:'Gross · deductions · net · currency by payslip' },
    { k:'leaves', label:'Leaves', desc:'All leave requests with type, dates and status' },
    { k:'attendance', label:'Attendance', desc:'Monthly present / absent / LWP / OT hours' },
    { k:'turnover', label:'Turnover', desc:'Terminated employees with hire → termination window' },
    { k:'assets', label:'Assets', desc:'Company assets, assignment status, purchase cost' },
  ];
  const currentPeriod = new Date().toISOString().slice(0, 7);
  const currentYear = new Date().getFullYear();
  const sendDigest = async () => {
    setDigestSending(true);
    try { const r = await apiClient.post('/hr/ceo-digest/send-now'); toast({ title:`Digest sent to ${r.data.sent} recipients` }); }
    catch(e){ toast({title:e?.response?.data?.detail||'Send failed',variant:'destructive'}); }
    finally { setDigestSending(false); }
  };
  return (
    <div>
      <H1 eyebrow="Global HR · §39" title="Management reports" sub="Download CSV extracts for board packs, statutory returns and BI tools." />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {reports.map((r)=>(
          <Card key={r.k} className="p-4">
            <FileDown className="w-5 h-5 text-[#0f5c3d] mb-2"/>
            <div className="text-sm font-semibold text-[#0b1a2e]">{r.label}</div>
            <p className="text-xs text-[#0b1a2e]/60 mt-1 min-h-[36px]">{r.desc}</p>
            <a href={`${API}/api/hr/reports/${r.k}.csv`} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs font-semibold" data-testid={`report-dl-${r.k}`}>
              <FileDown className="w-3.5 h-3.5"/> Download CSV
            </a>
          </Card>
        ))}
      </div>

      <div className="mt-8">
        <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#0f5c3d] mb-3">Statutory filings</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {statutory.map((f)=>{
            const val = f.param === 'year' ? currentYear : currentPeriod;
            return (
              <Card key={f.code} className="p-4 border-amber-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800">{f.country}</span>
                  <span className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/50 font-semibold">{f.authority}</span>
                </div>
                <div className="text-sm font-semibold text-[#0b1a2e]">{f.label}</div>
                <p className="text-xs text-[#0b1a2e]/60 mt-1 mb-3">Period: <span className="font-mono">{val}</span></p>
                <a href={`${API}${f.endpoint}?${f.param}=${val}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0b1a2e] text-white text-xs font-semibold" data-testid={`stat-${f.code}`}>
                  <FileDown className="w-3.5 h-3.5"/> Export
                </a>
              </Card>
            );
          })}
        </div>
      </div>

      <div className="mt-8">
        <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#0f5c3d] mb-3">CEO weekly digest</div>
        <Card className="p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <div className="text-sm font-semibold text-[#0b1a2e]">Weekly workforce digest</div>
            <p className="text-xs text-[#0b1a2e]/60 mt-1">Auto-emails every Monday 06:00 UTC to super_admin + admin roles. Contains the Global Dashboard snapshot: headcount, joiners/leavers, vacancies, payroll, pending approvals, and 90-day alerts.</p>
          </div>
          <a href={`${API}/api/hr/ceo-digest/preview.html`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#0f5c3d]/20 text-[#0f5c3d] text-xs font-semibold" data-testid="digest-preview">Preview</a>
          <Btn onClick={sendDigest} disabled={digestSending} data-testid="digest-send-now">{digestSending?<Loader2 className="w-3.5 h-3.5 animate-spin"/>:<FileDown className="w-3.5 h-3.5"/>} Send now</Btn>
        </Card>
      </div>
    </div>
  );
};

// ============================================================
// DATA MIGRATION
// ============================================================
export const MigrationPage = () => {
  const [file, setFile] = useState(null); const [preview, setPreview] = useState(null); const [committing, setCommitting] = useState(false); const [result, setResult] = useState(null);
  const doPreview = async () => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const r = await apiClient.post('/hr/migration/employees/preview', fd, {headers:{'Content-Type':'multipart/form-data'}}); setPreview(r.data); }
    catch(e){ toast({title:e?.response?.data?.detail||'Preview failed',variant:'destructive'}); }
  };
  const doCommit = async () => {
    if (!file) return; setCommitting(true);
    const fd = new FormData(); fd.append('file', file); fd.append('default_role','staff');
    try { const r = await apiClient.post('/hr/migration/employees/commit', fd, {headers:{'Content-Type':'multipart/form-data'}}); setResult(r.data); toast({title:`${r.data.inserted} imported`}); }
    catch(e){ toast({title:e?.response?.data?.detail||'Import failed',variant:'destructive'}); }
    finally { setCommitting(false); }
  };
  return (
    <div>
      <H1 eyebrow="Global HR · §47" title="Data migration" sub="Bulk-import employees from Excel/CSV. Expected columns: name, email, role, country, department, designation, employee_code, hire_date, salary_amount, salary_currency." />
      <Card className="p-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div className="sm:col-span-2"><Field label="CSV file"><input type="file" accept=".csv" onChange={(e)=>{setFile(e.target.files?.[0]||null);setPreview(null);setResult(null);}} data-testid="mig-file" className="w-full text-xs border border-slate-300 rounded p-2"/></Field></div>
          <div className="flex gap-2"><Btn variant="ghost" onClick={doPreview} disabled={!file} data-testid="mig-preview-btn"><UploadCloud className="w-3.5 h-3.5"/> Preview</Btn><Btn onClick={doCommit} disabled={!file||committing} data-testid="mig-commit-btn">{committing?<Loader2 className="w-3.5 h-3.5 animate-spin"/>:<Check className="w-3.5 h-3.5"/>} Import</Btn></div>
        </div>
      </Card>
      {preview && (
        <Card className="mt-4 p-5">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#0f5c3d] mb-3">Preview · {preview.total_rows} rows · {preview.valid_rows} valid · {preview.duplicate_rows} duplicates</div>
          <div className="overflow-x-auto max-h-[60vh]">
            <table className="w-full text-xs">
              <thead className="bg-[#f7fbf8]"><tr>{preview.columns.map((c)=><th key={c} className="text-left p-1.5">{c}</th>)}<th className="text-left p-1.5">status</th></tr></thead>
              <tbody>{(preview.preview||[]).map((r,i)=>(
                <tr key={i} className={`border-t border-black/5 ${r._duplicate?'bg-amber-50':!r._valid?'bg-red-50':''}`}>
                  {preview.columns.map((c)=><td key={c} className="p-1.5">{r[c]}</td>)}
                  <td className="p-1.5">{r._duplicate?<span className="text-amber-700">duplicate</span>:r._valid?<span className="text-emerald-700">new</span>:<span className="text-red-700">invalid</span>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </Card>
      )}
      {result && (
        <Card className="mt-4 p-5 bg-emerald-50 border-emerald-200">
          <div className="text-sm font-semibold text-emerald-800">Import complete: {result.inserted} new · {result.skipped_duplicates} skipped · {result.errors?.length||0} errors</div>
          {result.errors?.length>0 && <div className="mt-2 text-xs">{result.errors.slice(0,10).map((e,i)=><div key={i}>Row {e.row}: {e.error}</div>)}</div>}
        </Card>
      )}
    </div>
  );
};

// ============================================================
// ACCESS MATRIX (RACI)
// ============================================================
export const AccessMatrixPage = () => {
  const [d, setD] = useState(null); const [saving, setSaving] = useState(false);
  const load = useCallback(async () => { const r = await apiClient.get('/hr/access-matrix'); setD(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = (role, mod, perm) => {
    const m = JSON.parse(JSON.stringify(d.matrix));
    m[role] = m[role] || {};
    m[role][mod] = m[role][mod] || { view:false, edit:false, approve:false };
    m[role][mod][perm] = !m[role][mod][perm];
    setD({ ...d, matrix: m });
  };
  const save = async () => { setSaving(true); try { await apiClient.put('/hr/access-matrix', {matrix:d.matrix}); toast({title:'Saved'}); } catch(e){ toast({title:'Failed',variant:'destructive'}); } finally { setSaving(false); } };
  const reset = async () => { if (!window.confirm('Reset to defaults?')) return; await apiClient.post('/hr/access-matrix/reset'); load(); };
  if (!d) return <div className="p-10 text-center"><Loader2 className="w-5 h-5 inline animate-spin"/></div>;
  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <H1 eyebrow="Global HR · §40" title="Access matrix (RACI)" sub="Top-down role × module permission grid. Overrides live per-role; org-hierarchy scoping applies on top for delegation." />
        <div className="flex gap-2 mb-6"><Btn variant="ghost" onClick={reset} data-testid="matrix-reset">Reset defaults</Btn><Btn onClick={save} disabled={saving} data-testid="matrix-save"><ShieldCheck className="w-3.5 h-3.5"/> {saving?'Saving…':'Save matrix'}</Btn></div>
      </div>
      <Card className="overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#f7fbf8]"><tr>
            <th className="text-left p-2 sticky left-0 bg-[#f7fbf8] z-10">Module</th>
            {d.roles.map((r)=><th key={r} colSpan={3} className="p-2 text-center uppercase tracking-widest text-[10px] font-semibold border-l border-black/5">{r}</th>)}
          </tr><tr className="bg-white text-[9px] uppercase tracking-widest text-[#0b1a2e]/60">
            <th className="sticky left-0 bg-white z-10"></th>
            {d.roles.map((r)=>d.perms.map((p)=><th key={`${r}-${p}`} className="p-1 border-l border-black/5">{p[0]}</th>))}
          </tr></thead>
          <tbody>{d.modules.map((m)=>(
            <tr key={m} className="border-t border-black/5" data-testid={`matrix-row-${m}`}>
              <td className="p-2 sticky left-0 bg-white font-mono text-[11px]">{m}</td>
              {d.roles.map((r)=>d.perms.map((p)=>{
                const v = !!d.matrix?.[r]?.[m]?.[p];
                return (<td key={`${r}-${m}-${p}`} className={`p-1 text-center border-l border-black/5 ${v?'bg-emerald-50':''}`}>
                  <input type="checkbox" checked={v} onChange={()=>toggle(r,m,p)} data-testid={`matrix-cell-${r}-${m}-${p}`}/>
                </td>);
              }))}
            </tr>
          ))}</tbody>
        </table>
      </Card>
    </div>
  );
};
